import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Cell, PieChart, ResponsiveContainer } from 'recharts';
import { 
  calculateWaterFootprint, 
  generateRecommendations,
  calculateSavings
} from './utils';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

const Results = ({ formData, resetCalculator }) => {
  const [waterResult, setWaterResult] = useState(null);
  const [selectedRecommendations, setSelectedRecommendations] = useState([]);
  const [recommendationsList, setRecommendationsList] = useState([]);
  const [savingsResult, setSavingsResult] = useState(null);
  const [pledgeAccepted, setPledgeAccepted] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);

  useEffect(() => {
    // Calculate and save results when component mounts
    const result = calculateWaterFootprint(formData);
    setWaterResult(result);
    
    // Generate recommendations based on the user's data
    const recommendations = generateRecommendations(formData, result);
    setRecommendationsList(recommendations);
  }, [formData]);    

  // Update savings calculation when recommendations are selected
  useEffect(() => {
    if (waterResult && selectedRecommendations.length > 0) {
      const savings = calculateSavings(waterResult, selectedRecommendations, recommendationsList);
      setSavingsResult(savings);
    } else {
      setSavingsResult(null);
    }
  }, [selectedRecommendations, waterResult, recommendationsList]);

  const handleRetake = () => {
    if (window.confirm('Are you sure you want to retake the calculator? All current data will be lost.')) {
      resetCalculator();
    }
  };

  const handleRecommendationToggle = (recId) => {
    setSelectedRecommendations(prev => 
      prev.includes(recId) 
        ? prev.filter(p => p !== recId)
        : [...prev, recId]
    );
  };

  const handlePledgeAcceptance = () => {
    if (selectedRecommendations.length === 0) {
      alert('Please select at least one recommendation to pledge.');
      return;
    }
    setPledgeAccepted(true);
  };

  // Handle section selection for detailed breakdown
  const handleSectionClick = (data) => {
    if (data && data.name) {
      setSelectedSection(data.name);
    }
  };
  
  const handleBackClick = () => {
    setSelectedSection(null);
  };

  if (!waterResult) {
    return <div className="text-center p-8">Loading results...</div>;
  }

  // Prepare chart data for main pie chart
  const mainChartData = [
    { 
      name: 'Indoor Usage', 
      value: waterResult.indoorPercentage, 
      color: 'rgba(46, 117, 182, 0.8)' 
    },
    { 
      name: 'Outdoor Usage', 
      value: waterResult.outdoorPercentage, 
      color: 'rgba(58, 175, 169, 0.8)' 
    }
  ];

  // Prepare detailed breakdown data based on the selected section
  const getDetailedData = () => {
    if (selectedSection === 'Indoor Usage') {
      // Create data for indoor usage breakdown
      const details = [];
      
      if (waterResult.indoor.userInteracted.bathing) {
        details.push({
          name: 'Bathroom',
          value: (waterResult.indoor.details.bathing / waterResult.indoor.annualLiters) * 100,
          liters: waterResult.indoor.details.bathing
        });
      }
      
      if (waterResult.indoor.userInteracted.toilet) {
        details.push({
          name: 'Toilet',
          value: (waterResult.indoor.details.toilet / waterResult.indoor.annualLiters) * 100,
          liters: waterResult.indoor.details.toilet
        });
      }
      
      if (waterResult.indoor.userInteracted.faucet) {
        details.push({
          name: 'Faucet',
          value: (waterResult.indoor.details.faucet / waterResult.indoor.annualLiters) * 100,
          liters: waterResult.indoor.details.faucet
        });
      }
      
      if (waterResult.indoor.userInteracted.dishwashing) {
        details.push({
          name: 'Dishwashing',
          value: (waterResult.indoor.details.dishwashing / waterResult.indoor.annualLiters) * 100,
          liters: waterResult.indoor.details.dishwashing
        });
      }
      
      if (waterResult.indoor.userInteracted.laundry) {
        details.push({
          name: 'Laundry',
          value: (waterResult.indoor.details.laundry / waterResult.indoor.annualLiters) * 100,
          liters: waterResult.indoor.details.laundry
        });
      }
      
      return details;
    } else if (selectedSection === 'Outdoor Usage') {
      // Create data for outdoor usage breakdown
      const details = [];
      
      if (formData.outdoor?.hasGarden && waterResult.outdoor.details?.garden) {
        details.push({
          name: 'Garden/Plants',
          value: (waterResult.outdoor.details.garden / waterResult.outdoor.annualLiters) * 100,
          liters: waterResult.outdoor.details.garden
        });
      }
      
      if (formData.outdoor?.hasLawn && waterResult.outdoor.details?.lawn) {
        details.push({
          name: 'Lawn',
          value: (waterResult.outdoor.details.lawn / waterResult.outdoor.annualLiters) * 100,
          liters: waterResult.outdoor.details.lawn
        });
      }
      
      if (formData.outdoor?.carWashMethod && formData.outdoor.carWashMethod !== 'never' && waterResult.outdoor.details?.carWash) {
        details.push({
          name: 'Car Washing',
          value: (waterResult.outdoor.details.carWash / waterResult.outdoor.annualLiters) * 100,
          liters: waterResult.outdoor.details.carWash
        });
      }
      
      return details;
    }
    
    return [];
  };

  // Get water saving tips based on the selected section
  const getWaterSavingTips = () => {
    if (selectedSection === 'Indoor Usage') {
      return [
        'Turn off the tap while brushing teeth or shaving',
        'Fix leaky faucets promptly - a small drip can waste thousands of liters',
        'Take shorter showers - aim for 5 minutes or less',
        'Install water-efficient showerheads and faucet aerators',
        'Only run dishwashers and washing machines with full loads'
      ];
    } else if (selectedSection === 'Outdoor Usage') {
      return [
        'Water your garden and lawn in the early morning to reduce evaporation',
        'Use mulch in garden beds to retain moisture',
        'Consider installing a drip irrigation system',
        'Use a bucket instead of a running hose when washing your car',
        'Collect rainwater for garden watering'
      ];
    }
    
    return [
      'Check for leaks regularly in your plumbing system',
      'Consider water-efficient appliances for your next upgrade',
      'Being mindful of water usage helps conserve this precious resource'
    ];
  };

  // Custom color palette for detailed charts
  const COLORS = [
    'rgba(46, 117, 182, 0.8)',
    'rgba(75, 192, 192, 0.8)',
    'rgba(153, 102, 255, 0.8)',
    'rgba(255, 159, 64, 0.8)',
    'rgba(255, 99, 132, 0.8)'
  ];

  return (
    <div className="section-container max-w-4xl mx-auto">
      {/* Header with retake button */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-blue-700">Your Water Footprint Results</h2>
          <p className="text-lg text-gray-600 mt-2">
            Hey {formData.personal?.name || 'there'}! Here's your water usage analysis.
          </p>
        </div>
        <button
          onClick={handleRetake}
          className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
        >
          Retake Calculator
        </button>
      </div>

      {/* Total Water Usage Summary */}
      <div className="text-center mb-8 bg-white p-6 rounded-lg shadow-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border-r border-gray-200">
            <h3 className="text-4xl font-bold text-blue-600 flex items-baseline justify-center">
              {Math.round(waterResult.totalDailyLitersPerPerson)}
              <span className="text-2xl text-gray-500 ml-2">Liters</span>
            </h3>
            <p className="text-xl text-gray-600 mt-2">Daily Water Usage Per Person</p>
          </div>
          <div>
            <h3 className="text-4xl font-bold text-blue-600 flex items-baseline justify-center">
              {Math.round(waterResult.totalAnnualLiters).toLocaleString()}
              <span className="text-2xl text-gray-500 ml-2">Liters</span>
            </h3>
            <p className="text-xl text-gray-600 mt-2">Annual Household Water Usage</p>
          </div>
        </div>
      </div>

      {/* Interactive Water Usage Breakdown Chart */}
      <div className="bg-white p-6 rounded-lg shadow-lg mb-8">
        {selectedSection ? (
          <div className="text-center mb-4">
            <h3 className="text-xl font-semibold text-blue-700">{selectedSection} Breakdown</h3>
            <button 
              onClick={handleBackClick}
              className="mt-2 text-blue-600 underline cursor-pointer"
            >
              Back to main view
            </button>
          </div>
        ) : (
          <h3 className="text-lg font-semibold mb-4 text-center">Water Usage Breakdown</h3>
        )}
        
        <div className="mx-auto" style={{ height: "400px", maxWidth: "700px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              {selectedSection ? (
                <Pie
                  data={getDetailedData()}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={150}
                  fill="#8884d8"
                  paddingAngle={2}
                  label={({name, value}) => `${name}: ${value.toFixed(1)}%`}
                  labelLine={true}
                >
                  {getDetailedData().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
              ) : (
                <Pie
                  data={mainChartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={150}
                  fill="#8884d8"
                  paddingAngle={2}
                  onClick={handleSectionClick}
                  cursor="pointer"
                  label={({name, value}) => `${name}: ${value.toFixed(1)}%`}
                  labelLine={true}
                >
                  {mainChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              )}
            </PieChart>
          </ResponsiveContainer>
        </div>
        
        {selectedSection && (
          <div className="mt-4">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Annual Usage</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Percentage</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getDetailedData().map((item, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center">
                          <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                          {item.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {Math.round(item.liters).toLocaleString()} liters
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.value.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {!selectedSection && (
          <p className="text-sm text-gray-500 mt-4 text-center">
            Click on a section in the chart to see a detailed breakdown
          </p>
        )}
      </div>

      {/* Detailed Breakdown */}
      <div className="bg-white p-6 rounded-lg shadow-lg mb-8">
        <h3 className="text-xl font-semibold mb-4">Detailed Breakdown</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-blue-600 mb-2">Indoor Water Usage</h4>
            <ul className="space-y-2">
              <li className="flex justify-between border-b pb-2">
                <span>Daily per person:</span>
                <span className="font-medium">{waterResult.indoor.dailyLitersPerPerson.toFixed(1)} liters</span>
              </li>
              <li className="flex justify-between border-b pb-2">
                <span>Annual household:</span>
                <span className="font-medium">{Math.round(waterResult.indoor.annualLiters).toLocaleString()} liters</span>
              </li>
              
              {waterResult.indoor.userInteracted.bathing && (
                <li className="flex justify-between border-b pb-2">
                  <span>Bathroom usage:</span>
                  <span className="font-medium">{Math.round(waterResult.indoor.details.bathing).toLocaleString()} liters/year</span>
                </li>
              )}
              
              {waterResult.indoor.userInteracted.toilet && (
                <li className="flex justify-between border-b pb-2">
                  <span>Toilet:</span>
                  <span className="font-medium">{Math.round(waterResult.indoor.details.toilet).toLocaleString()} liters/year</span>
                </li>
              )}
              
              {waterResult.indoor.userInteracted.faucet && (
                <li className="flex justify-between border-b pb-2">
                  <span>Faucet usage:</span>
                  <span className="font-medium">{Math.round(waterResult.indoor.details.faucet).toLocaleString()} liters/year</span>
                </li>
              )}
              
              {waterResult.indoor.userInteracted.dishwashing && (
                <li className="flex justify-between border-b pb-2">
                  <span>Dishwashing:</span>
                  <span className="font-medium">{Math.round(waterResult.indoor.details.dishwashing).toLocaleString()} liters/year</span>
                </li>
              )}
              
              {waterResult.indoor.userInteracted.laundry && (
                <li className="flex justify-between border-b pb-2">
                  <span>Laundry:</span>
                  <span className="font-medium">{Math.round(waterResult.indoor.details.laundry).toLocaleString()} liters/year</span>
                </li>
              )}
              
              <li className="flex justify-between">
                <span>Percentage of total:</span>
                <span className="font-medium">{waterResult.indoorPercentage.toFixed(1)}%</span>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-teal-600 mb-2">Outdoor Water Usage</h4>
            <ul className="space-y-2">
              <li className="flex justify-between border-b pb-2">
                <span>Monthly average:</span>
                <span className="font-medium">{Math.round(waterResult.outdoor.monthlyLiters).toLocaleString()} liters</span>
              </li>
              <li className="flex justify-between border-b pb-2">
                <span>Annual total:</span>
                <span className="font-medium">{Math.round(waterResult.outdoor.annualLiters).toLocaleString()} liters</span>
              </li>
              
              {formData.outdoor?.hasGarden && (
                <li className="flex justify-between border-b pb-2">
                  <span>Garden/Plants:</span>
                  <span className="font-medium">{Math.round(waterResult.outdoor.details?.garden || 0).toLocaleString()} liters/year</span>
                </li>
              )}
              
              {formData.outdoor?.hasLawn && (
                <li className="flex justify-between border-b pb-2">
                  <span>Lawn watering:</span>
                  <span className="font-medium">{Math.round(waterResult.outdoor.details?.lawn || 0).toLocaleString()} liters/year</span>
                </li>
              )}
              
              {formData.outdoor?.carWashMethod && formData.outdoor.carWashMethod !== 'never' && (
                <li className="flex justify-between border-b pb-2">
                  <span>Car washing:</span>
                  <span className="font-medium">
                    {Math.round(waterResult.outdoor.details?.carWash || 0).toLocaleString()} liters/year
                  </span>
                </li>
              )}
              
              <li className="flex justify-between">
                <span>Percentage of total:</span>
                <span className="font-medium">{waterResult.outdoorPercentage.toFixed(1)}%</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Recommendations Section */}
      <div className="bg-white p-6 rounded-lg shadow-lg mb-8">
        <h3 className="text-xl font-semibold mb-4">Water-Saving Recommendations</h3>
        
        {!pledgeAccepted ? (
          <>
            <p className="text-gray-600 mb-4">
              Select the water-saving actions you'd like to commit to:
            </p>
            <div className="space-y-3">
              {recommendationsList.map((rec, index) => (
                <div 
                  key={index} 
                  onClick={() => handleRecommendationToggle(rec.id)}
                  className={`p-4 rounded-lg border transition-colors cursor-pointer
                    ${selectedRecommendations.includes(rec.id) 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-blue-300'}`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedRecommendations.includes(rec.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleRecommendationToggle(rec.id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1 h-4 w-4 text-blue-600 rounded cursor-pointer"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-900">{rec.title}</h4>
                        {rec.cost && (
                          <span className="text-sm font-medium text-gray-600">
                            {rec.cost}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{rec.description}</p>
                      <p className="text-xs text-blue-600 font-medium mt-2">
                        Potential savings: {Math.round(rec.savingsLiters).toLocaleString()} liters per year
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {savingsResult && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Potential Impact of Your Selected Actions</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-gray-600">Water Saved</p>
                    <p className="text-xl font-bold text-blue-600">
                      {Math.round(savingsResult.savings.annualLiters).toLocaleString()} liters/year
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Reduction</p>
                    <p className="text-xl font-bold text-blue-600">
                      {savingsResult.savings.percentReduction.toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">New Daily Usage</p>
                    <p className="text-xl font-bold text-blue-600">
                      {savingsResult.newFootprint.totalDailyLitersPerPerson.toFixed(1)} liters/person
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-center mt-6">
              <button
                onClick={handlePledgeAcceptance}
                disabled={selectedRecommendations.length === 0}
                className={`px-6 py-3 rounded-lg font-medium text-white
                  ${selectedRecommendations.length === 0 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700 transition-colors'}`}
              >
                I Pledge to Save Water
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-6 space-y-4">
            <div className="text-blue-600 text-6xl mb-4">💧</div>
            <h4 className="text-2xl font-bold text-blue-600">Thank You for Your Pledge!</h4>
            <p className="text-gray-700">
              By committing to these{selectedRecommendations.length === 1 
                ? ' changes' 
                : ` ${selectedRecommendations.length} changes`}, you'll help conserve our planet's most precious resource.
            </p>
            
            {savingsResult && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="font-medium">Your impact:</p>
                <p className="text-lg">
                  You've pledged to save <span className="font-bold text-blue-600">{Math.round(savingsResult.savings.annualLiters).toLocaleString()} liters</span> of water per year.
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  That's equivalent to {Math.round(savingsResult.savings.annualLiters / 9.5)} minutes of showering
                  or {Math.round(savingsResult.savings.annualLiters / 0.5).toLocaleString()} bottles of water!
                </p>
              </div>
            )}
            
            <div className="flex justify-center mt-6">
              <button
                onClick={() => setPledgeAccepted(false)}
                className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Update My Pledge
              </button>
            </div>
          </div>
        )}
      </div>


    </div>
  );
};

export default Results;